if (!customElements.get('bn-featured-collection')) {
  customElements.define('bn-featured-collection', class BnFeaturedCollection extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.sliderComponent = this.querySelector('[data-bn-fc-slider]');
      this.list = this.querySelector('[data-bn-fc-list]');
      this.nativePrev = this.querySelector('[data-bn-fc-native-controls] .slider-button--prev');
      this.nativeNext = this.querySelector('[data-bn-fc-native-controls] .slider-button--next');
      this.nativeCurrent = this.querySelector('[data-bn-fc-native-controls] .slider-counter--current');
      this.nativeTotal = this.querySelector('[data-bn-fc-native-controls] .slider-counter--total');
      this.prev = this.querySelector('[data-bn-fc-prev]');
      this.next = this.querySelector('[data-bn-fc-next]');
      this.current = this.querySelector('[data-bn-fc-current]');
      this.total = this.querySelector('[data-bn-fc-total]');
      this.progress = this.querySelector('[data-bn-fc-progress]');
      this.autoplayTimer = null;
      this.inView = true;
      this.setupReveal();
      this.setupPagination();
      this.setupAutoplay();
      this.setupThemeEditor();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.entryObserver?.disconnect();
      this.activityObserver?.disconnect();
      this.counterObserver?.disconnect();
      this.stopAutoplay();
    }

    setupReveal() {
      if (!this.classList.contains('bn-fc-motion-armed')) return;
      const reveal = () => this.classList.add('bn-fc-in-view');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.isConnected) return;
        if (!('IntersectionObserver' in window) || window.Shopify?.designMode) { reveal(); return; }
        this.entryObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.classList.toggle('bn-fc-in-view', entry.isIntersecting);
        }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
        this.entryObserver.observe(this);
      }));
    }

    setupPagination() {
      if (!this.prev || !this.next || !this.nativePrev || !this.nativeNext) return;
      this.prev.addEventListener('click', () => { this.retreat(); }, { signal: this.abortController.signal });
      this.next.addEventListener('click', () => { this.advance(); }, { signal: this.abortController.signal });
      this.list?.addEventListener('scroll', () => this.scheduleSync(), { passive: true, signal: this.abortController.signal });
      if (this.nativeCurrent && 'MutationObserver' in window) {
        this.counterObserver = new MutationObserver(() => this.syncPagination());
        this.counterObserver.observe(this.nativeCurrent, { childList: true, characterData: true, subtree: true });
      }
      this.syncPagination();
    }

    afterNavigation() {
      this.scheduleSync();
      this.restartAutoplay();
    }

    scheduleSync() {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => this.syncPagination(), 80);
    }

    syncPagination() {
      const cur = Math.max(1, Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1);
      const total = Math.max(cur, Number.parseInt(this.nativeTotal?.textContent || '1', 10) || 1);
      if (this.current) this.current.textContent = String(cur).padStart(2, '0');
      if (this.total) this.total.textContent = String(total).padStart(2, '0');
      if (this.progress) this.progress.style.width = `${Math.min(100, (cur / total) * 100)}%`;
    }

    retreat() {
      const cur = Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1;
      if (this.nativePrev.disabled || cur <= 1) {
        if (this.list) {
          const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
          this.list.scrollTo({ left: maxScroll, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
        }
        this.restartAutoplay();
        setTimeout(() => this.syncPagination(), 360);
      } else {
        this.nativePrev.click();
        this.afterNavigation();
      }
    }

    advance() {
      const cur = Number.parseInt(this.nativeCurrent?.textContent || '1', 10) || 1;
      const total = Number.parseInt(this.nativeTotal?.textContent || '1', 10) || 1;
      if (this.nativeNext.disabled || cur >= total) {
        if (this.list) this.list.scrollTo({ left: 0, behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
        this.restartAutoplay();
        setTimeout(() => this.syncPagination(), 360);
      } else {
        this.nativeNext.click();
        this.afterNavigation();
      }
    }

    autoplayEnabled() {
      const w = window.innerWidth;
      const sliderActive = w < 990 ? this.dataset.mobileSlider === 'true' : this.dataset.desktopSlider === 'true';
      if (!sliderActive) return false;
      if (w < 750) return this.dataset.autoplayMobile === 'true';
      if (w < 990) return this.dataset.autoplayTablet === 'true';
      return this.dataset.autoplayDesktop === 'true';
    }

    setupAutoplay() {
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: this.abortController.signal });
      window.addEventListener('resize', () => this.syncAutoplay(), { passive: true, signal: this.abortController.signal });
      if ('IntersectionObserver' in window) {
        this.inView = false;
        this.activityObserver = new IntersectionObserver((entries) => {
          this.inView = !!entries[0]?.isIntersecting;
          this.syncAutoplay();
        }, { threshold: 0.08 });
        this.activityObserver.observe(this);
      }
      this.syncAutoplay();
    }

    stopAutoplay() { if (this.autoplayTimer) clearTimeout(this.autoplayTimer); this.autoplayTimer = null; }
    restartAutoplay() { this.stopAutoplay(); this.syncAutoplay(); }
    syncAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled() || !this.prev || !this.next || !this.inView || document.hidden) return;
      const delay = Math.max(4000, Number.parseInt(this.dataset.autoplaySpeed || '6000', 10));
      this.autoplayTimer = setTimeout(() => { this.autoplayTimer = null; this.advance(); this.syncAutoplay(); }, delay);
    }

    setupThemeEditor() {
      this.addEventListener('shopify:section:select', () => {
        if (!this.classList.contains('bn-fc-motion-armed')) return;
        this.classList.remove('bn-fc-in-view');
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('bn-fc-in-view')));
      }, { signal: this.abortController.signal });
    }
  });
}
